import React, { useMemo } from 'react'
import { useAccount } from '../../../../common/useAccount'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppDispatch } from '../../../../store/hooks'
import { addToast } from '../../../../store/toast/toastSlice'
import { useResetLogoFileMutation, useUpdateLogoFileMutation } from '../../../../store/account/account'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { type BrandingConfig } from '../../../../models/Account'
import { BRAND_COLOR_PRESETS } from '../../../Scheduling/shared/schedulingDisplay'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { sanitizeAccountConfig } from '../Account/sanitizeAccountConfig'
import { DisplayNameSection } from './DisplayNameSection'
import { BrandingLogoSection } from './BrandingLogoSection'
import { BrandColorSection } from './BrandColorSection'
import { BRANDING_LOGO_RESET_KEY, BRANDING_LOGO_UPDATE_KEY } from './brandingConstants'

export const BrandingSettings: React.FC = () => {
  const { accountId, account, isFetching } = useAccount()
  const [updateAccount, { isLoading: isSaving }] = useUpdateAccountMutation()
  const [, { isLoading: isUpdatingLogo }] = useUpdateLogoFileMutation({ fixedCacheKey: BRANDING_LOGO_UPDATE_KEY })
  const [, { isLoading: isResettingLogo }] = useResetLogoFileMutation({ fixedCacheKey: BRANDING_LOGO_RESET_KEY })
  const dispatch = useAppDispatch()
  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery(
    { accountId, identityTypes: ['idp'] },
    { refetchOnMountOrArgChange: true, skip: !accountId },
  )
  const validUserIds = useMemo(() => (usersData && !isUsersLoading ? new Set(usersData.data.map((user) => user.id)) : null), [usersData, isUsersLoading])

  const branding = account?.effectiveConfig.branding
  const isBrandingEnforced = account?.enterpriseConfig.branding != null
  const isNameLocked = account?.enterpriseConfig.lockAccountName === true
  const isAccountBusy = isSaving || isFetching
  const isConfigBusy = isAccountBusy || isUpdatingLogo || isResettingLogo

  const saveBranding = async (patch: Partial<BrandingConfig>): Promise<boolean> => {
    if (!account) {
      return false
    }
    // The IAM PATCH replaces the whole config, so always send the full merged object.
    const config = sanitizeAccountConfig({ ...account.config, branding: { ...(account.config.branding ?? {}), ...patch } }, validUserIds)
    const result = await updateAccount({ accountId, payload: { config } })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not save branding', messages: ['Please try again.'] }))
      return false
    }
    return true
  }

  return (
    <div className='d-flex flex-column zocks-gap-2'>
      <div className='d-flex flex-column zocks-gap-0.25'>
        <h4 className='zocks-text-gray-900'>Branding</h4>
        <p className='paragraph-medium zocks-text-gray-700 m-0'>Set how your firm appears on client-facing pages and documents.</p>
      </div>
      {account ? (
        <>
          {isBrandingEnforced ? <EnterpriseManagedBanner message='Branding is managed at the enterprise level.' /> : null}
          {isNameLocked && !isBrandingEnforced ? <EnterpriseManagedBanner message='Display name is locked at the enterprise level.' /> : null}
          <DisplayNameSection
            accountName={account.name}
            displayName={branding?.displayName ?? null}
            disabled={isBrandingEnforced || isNameLocked || isConfigBusy}
            onSave={(displayName) => saveBranding({ displayName })}
          />
          <BrandingLogoSection disabled={isAccountBusy} />
          <BrandColorSection
            brandColor={branding?.brandColor ?? BRAND_COLOR_PRESETS[0].hex}
            disabled={isBrandingEnforced || isConfigBusy}
            onSave={(brandColor) => saveBranding({ brandColor })}
          />
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center py-5'>
          <Spinner />
        </div>
      )}
    </div>
  )
}
